import DateStringInput from '@repo/ui/date-string-input'
import MultiSelectInput from '@repo/ui/multiselect-input'
import RadioInput from '@repo/ui/radio-input'
import SelectInput from '@repo/ui/select-input'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getCountries } from '../../api/countries/get-countries'
import { getAllCses } from '../../api/cse/cse'
import {
  BillingTypeEnum,
  billingTypeEnumMapping
} from '../../lib/types/enums/cse/billing-type.enum'
import { EnrollmentAccountingFilters } from '../../lib/types/models/enrollment/enrollment-accounting-filters'

type Props = {
  filter?: EnrollmentAccountingFilters
  onFilterChange: (filter: EnrollmentAccountingFilters) => void
}

const EnrollmentAccountingFilter: React.FC<Props> = ({ filter, onFilterChange }) => {
  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: cses } = useQuery({
    queryKey: ['cses', 'BASIC'],
    queryFn: () => getAllCses({ version: 'BASIC', limit: 1000 })
  })

  const selectedCse = cses?.data.find((cse) => cse.id === filter?.cseId)

  return (
    <div className="flex flex-col gap-4 mt-2">
      <MultiSelectInput
        searchable
        selectedOptions={filter?.countryCodes?.map((country) => ({
          label: countries?.data.find((c) => c.code === country)?.name || '',
          value: country
        }))}
        variant="primary"
        onChange={(countryCodes) => onFilterChange({ ...filter, countryCodes })}
        options={
          countries?.data.map((country) => ({
            label: country.name,
            value: country.code
          })) || []
        }
        label="Pays"
      />

      <DateStringInput
        label={'Date de confirmation de l’inscription'}
        value={filter?.confirmedAt}
        onChange={(date) => onFilterChange({ ...filter, confirmedAt: date })}
      />

      <div className="flex flex-col gap-2.5">
        <Typography variant="tag">Paiement</Typography>
        <div className="flex flex-col gap-3">
          <RadioInput
            checked={filter?.fullyPaid === undefined}
            label="Tout"
            onCheck={() => onFilterChange({ ...filter, fullyPaid: undefined })}
          />
          <RadioInput
            checked={filter?.fullyPaid === false}
            label="Pas entièrement payé"
            onCheck={() => onFilterChange({ ...filter, fullyPaid: false })}
          />
          <RadioInput
            checked={filter?.fullyPaid === true}
            label="Entièrement payé"
            onCheck={() => onFilterChange({ ...filter, fullyPaid: true })}
          />
        </div>
      </div>

      <MultiSelectInput
        searchable
        selectedOptions={filter?.cseBillingTypes?.map((billingType) => ({
          label: billingTypeEnumMapping[billingType],
          value: billingType
        }))}
        variant="primary"
        onChange={(cseBillingTypes) => onFilterChange({ ...filter, cseBillingTypes })}
        options={Object.values(BillingTypeEnum).map((billingType) => ({
          label: billingTypeEnumMapping[billingType],
          value: billingType
        }))}
        label="Type de facturation CSE"
      />

      <SelectInput
        searchable
        label="CSE"
        options={
          cses?.data.map((cse) => ({
            label: `${cse.code} - ${cse.label}`,
            value: cse.id
          })) || []
        }
        selectedOption={
          selectedCse
            ? { label: `${selectedCse.code} - ${selectedCse.label}`, value: selectedCse.id }
            : undefined
        }
        onChange={(cseId) => onFilterChange({ ...filter, cseId })}
      />
    </div>
  )
}

export default EnrollmentAccountingFilter
